import { useCallback, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useTwoFactorAuthStore } from '@/store/twoFactorAuth';
import { useWatchApprovalStatus } from './usePendingApprovals';

export type ProtectedActionType =
  | 'void_order'
  | 'refund'
  | 'cash_adjustment'
  | 'discount'
  | 'role_change'
  | 'user_deactivate'
  | 'primary_admin_change'
  | 'disable_2fa'
  | 'security_settings'
  | 'export_audit_logs';

interface ProtectedActionConfig {
  action_type: ProtectedActionType;
  requires_owner_2fa: boolean;
  threshold_amount: number | null;
  threshold_percentage: number | null;
}

interface ApprovalRequest {
  actionType: ProtectedActionType;
  metadata: Record<string, unknown>;
  description: string;
  amount?: number;
}

export function useOwner2FAApproval() {
  const { profile, user } = useAuth();
  const { requestOwnerApproval, clearOwnerApproval, isOwnerApprovalDialogOpen } = useTwoFactorAuthStore();
  const [isChecking, setIsChecking] = useState(false);
  const [pendingApprovalId, setPendingApprovalId] = useState<string | null>(null);
  const [pushApprovalCallbacks, setPushApprovalCallbacks] = useState<{
    onApproved: () => void;
    onDenied: () => void;
  } | null>(null);

  // Watch for push approval status changes
  const { status: watchedStatus } = useWatchApprovalStatus(pendingApprovalId);

  // Fetch protected action configs for the organization
  const { data: actionConfigs } = useQuery({
    queryKey: ['protected-actions', profile?.organization_id],
    queryFn: async (): Promise<ProtectedActionConfig[]> => {
      if (!profile?.organization_id) throw new Error('No organization');

      const { data, error } = await supabase
        .from('protected_actions')
        .select('*')
        .eq('organization_id', profile.organization_id);

      if (error) throw error;
      return data || [];
    },
    enabled: !!profile?.organization_id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Check if owner 2FA is enabled and get approval flow setting
  const { data: securitySettings } = useQuery({
    queryKey: ['owner-2fa-settings', profile?.organization_id],
    queryFn: async (): Promise<{ enabled: boolean; flow: 'inline' | 'push' }> => {
      if (!profile?.organization_id) return { enabled: false, flow: 'inline' };

      const { data, error } = await supabase
        .from('organizations')
        .select('security_settings')
        .eq('id', profile.organization_id)
        .single();

      if (error) return { enabled: false, flow: 'inline' };
      return {
        enabled: data?.security_settings?.owner_2fa_enabled ?? false,
        flow: data?.security_settings?.owner_2fa_approval_flow ?? 'inline',
      };
    },
    enabled: !!profile?.organization_id,
    staleTime: 5 * 60 * 1000,
  });

  const owner2FAEnabled = securitySettings?.enabled ?? false;
  const approvalFlow = securitySettings?.flow ?? 'inline';

  // Handle push approval status changes
  useEffect(() => {
    if (!watchedStatus || !pushApprovalCallbacks) return;

    if (watchedStatus === 'approved') {
      pushApprovalCallbacks.onApproved();
      setPendingApprovalId(null);
      setPushApprovalCallbacks(null);
    } else if (watchedStatus === 'denied' || watchedStatus === 'expired') {
      pushApprovalCallbacks.onDenied();
      setPendingApprovalId(null);
      setPushApprovalCallbacks(null);
    }
  }, [watchedStatus, pushApprovalCallbacks]);

  // Check if approval is required for an action
  const checkApprovalRequired = useCallback(
    async (actionType: ProtectedActionType, amount?: number): Promise<{ required: boolean; usePushFlow: boolean }> => {
      if (!profile?.organization_id || !owner2FAEnabled) {
        return { required: false, usePushFlow: false };
      }

      // If user is the owner/primary_admin, they can approve themselves with inline flow
      const isOwnerRole = profile.role === 'primary_admin' || profile.role === 'owner';

      const { data, error } = await supabase.rpc('check_owner_2fa_required', {
        p_organization_id: profile.organization_id,
        p_action_type: actionType,
        p_amount: amount ?? null,
      });

      if (error || !data?.[0]) return { required: false, usePushFlow: false };

      const required = data[0].requires_approval;
      // Use push flow only if enabled AND the requester is not an owner themselves
      const usePushFlow = required && approvalFlow === 'push' && !isOwnerRole;

      return { required, usePushFlow };
    },
    [profile, owner2FAEnabled, approvalFlow]
  );

  // Create a push approval request
  const createPushApprovalRequest = useCallback(
    async (request: ApprovalRequest): Promise<string | null> => {
      if (!user?.id || !profile?.organization_id) return null;

      const { data, error } = await supabase.rpc('create_pending_approval' as never, {
        p_organization_id: profile.organization_id,
        p_action_type: request.actionType,
        p_requester_user_id: user.id,
        p_action_metadata: {
          ...request.metadata,
          description: request.description,
          amount: request.amount,
        },
      } as never);

      if (error) {
        console.error('Failed to create push approval:', error);
        return null;
      }

      return (data as { approval_id?: string }[])?.[0]?.approval_id || null;
    },
    [user?.id, profile?.organization_id]
  );

  // Request owner approval for an action
  const requireApproval = useCallback(
    (request: ApprovalRequest): Promise<boolean> => {
      return new Promise((resolve) => {
        setIsChecking(true);

        checkApprovalRequired(request.actionType, request.amount).then(async (result) => {
          setIsChecking(false);

          if (!result.required) {
            resolve(true);
            return;
          }

          if (result.usePushFlow) {
            // Push flow: Create pending approval and wait for owner response
            const approvalId = await createPushApprovalRequest(request);
            if (!approvalId) {
              // Failed to create, fall back to inline
              requestOwnerApproval(
                request.actionType,
                request.metadata,
                request.description,
                () => resolve(true),
                () => resolve(false)
              );
              return;
            }

            // Store the approval ID and callbacks for watching
            setPendingApprovalId(approvalId);
            setPushApprovalCallbacks({
              onApproved: () => resolve(true),
              onDenied: () => resolve(false),
            });
          } else {
            // Inline flow: Show the approval dialog immediately
            requestOwnerApproval(
              request.actionType,
              request.metadata,
              request.description,
              () => resolve(true),
              () => resolve(false)
            );
          }
        });
      });
    },
    [checkApprovalRequired, requestOwnerApproval, createPushApprovalRequest]
  );

  // Verify owner's 2FA code and record approval
  const verifyAndApprove = useCallback(
    async (code: string): Promise<boolean> => {
      if (!user?.id || !profile?.organization_id) return false;

      const { pendingOwnerApproval } = useTwoFactorAuthStore.getState();
      if (!pendingOwnerApproval) return false;

      try {
        // In production, verify the TOTP code server-side
        // For now, accept any 6-digit code
        const isValidFormat = /^\d{6}$/.test(code);
        if (!isValidFormat) return false;

        // Record the approval
        const { error } = await supabase.rpc('record_owner_2fa_approval', {
          p_organization_id: profile.organization_id,
          p_action_type: pendingOwnerApproval.actionType,
          p_requester_user_id: user.id,
          p_approver_user_id: user.id, // Self-approval for owner
          p_action_metadata: pendingOwnerApproval.actionMetadata,
        });

        if (error) {
          console.error('Failed to record approval:', error);
          return false;
        }

        // Call the success callback
        pendingOwnerApproval.onApproved();
        clearOwnerApproval();
        return true;
      } catch (err) {
        console.error('Verification failed:', err);
        return false;
      }
    },
    [user, profile, clearOwnerApproval]
  );

  // Cancel the approval request
  const cancelApproval = useCallback(async () => {
    const { pendingOwnerApproval } = useTwoFactorAuthStore.getState();

    // If there's a push approval pending, cancel it
    if (pendingApprovalId) {
      await supabase
        .from('pending_approvals')
        .update({ status: 'expired', resolved_at: new Date().toISOString() } as never)
        .eq('id', pendingApprovalId as never);

      setPendingApprovalId(null);
      if (pushApprovalCallbacks) {
        pushApprovalCallbacks.onDenied();
        setPushApprovalCallbacks(null);
      }
    }

    // Handle inline approval
    if (pendingOwnerApproval) {
      pendingOwnerApproval.onDenied();
    }
    clearOwnerApproval();
  }, [clearOwnerApproval, pendingApprovalId, pushApprovalCallbacks]);

  // Get the config for a specific action
  const getActionConfig = useCallback(
    (actionType: ProtectedActionType): ProtectedActionConfig | undefined => {
      return actionConfigs?.find((c) => c.action_type === actionType);
    },
    [actionConfigs]
  );

  return {
    // State
    isOwnerApprovalDialogOpen,
    isChecking,
    owner2FAEnabled: owner2FAEnabled ?? false,
    actionConfigs: actionConfigs ?? [],
    approvalFlow,
    isPushApprovalPending: !!pendingApprovalId,

    // Actions
    requireApproval,
    verifyAndApprove,
    cancelApproval,
    checkApprovalRequired,
    getActionConfig,
  };
}
